import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  LogOut, Search, Download, Users, 
  Mail, FileText, Filter, ArrowUpDown, Clock
} from 'lucide-react';

import useAdminAuth from '../hooks/useAdminAuth';
import useSubmissions from '../hooks/useSubmissions';
import AdminLoginForm from '../components/AdminLoginForm';
import SubmissionsTable from '../components/SubmissionsTable';

const AdminPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const { 
    isAuthenticated, 
    loginLoading, 
    loginError, 
    login, 
    logout, 
    getAuthHeader 
  } = useAdminAuth();

  const { 
    submissions, 
    loading, 
    fetchSubmissions, 
    deleteSubmission, 
    updateSubmission 
  } = useSubmissions(getAuthHeader);

  // Fetch submissions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [isAuthenticated, fetchSubmissions]);

  const handleLogin = useCallback(async (user, pass) => {
    const success = await login(user, pass);
    if (success) {
      setUsername('');
      setPassword('');
    }
  }, [login]);

  const handleLogout = useCallback(() => {
    logout();
    setUsername('');
    setPassword('');
  }, [logout]);

  // Filter and sort submissions
  const filteredSubmissions = useMemo(() => {
    return submissions
      .filter(s => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = searchTerm === '' || 
          (s.name || '').toLowerCase().includes(searchLower) ||
          (s.email || '').toLowerCase().includes(searchLower) ||
          (s.subject || '').toLowerCase().includes(searchLower) ||
          (s.condition || '').toLowerCase().includes(searchLower);
        
        const matchesType = filterType === 'all' || s.type === filterType;
        
        return matchesSearch && matchesType;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'newest': return new Date(b.timestamp) - new Date(a.timestamp);
          case 'oldest': return new Date(a.timestamp) - new Date(b.timestamp);
          case 'name-az': return (a.name || '').localeCompare(b.name || '');
          case 'name-za': return (b.name || '').localeCompare(a.name || '');
          case 'priority': {
            const priorityOrder = { 'High': 0, 'Normal': 1, 'Low': 2 };
            const aRank = priorityOrder[a.priority] ?? 1;
            const bRank = priorityOrder[b.priority] ?? 1;
            return aRank - bRank;
          }
          default: return 0;
        }
      });
  }, [submissions, searchTerm, filterType, sortBy]);

  const exportToCSV = useCallback(() => {
    const headers = ['Type', 'Name', 'Email', 'Phone', 'Subject/Condition', 'Message', 'Date', 'Priority', 'Status'];
    const rows = filteredSubmissions.map(s => [
      s.type === 'join_study' ? 'Study Registration' : 'Contact',
      s.name || `${s.firstName} ${s.lastName}`,
      s.email,
      s.phone || '',
      s.type === 'join_study' ? s.condition : s.subject,
      s.message || '',
      new Date(s.timestamp).toLocaleDateString(),
      s.priority || 'Normal',
      s.status || 'New'
    ]);
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cdoc-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredSubmissions]);

  // Stats calculations
  const stats = useMemo(() => ({
    total: submissions.length,
    studyRegistrations: submissions.filter(s => s.type === 'join_study').length,
    contactMessages: submissions.filter(s => s.type === 'contact').length,
    newPending: submissions.filter(s => s.status === 'New').length
  }), [submissions]);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <AdminLoginForm
        username={username}
        setUsername={setUsername}
        password={password}
        setPassword={setPassword}
        onSubmit={handleLogin}
        loginError={loginError}
        loginLoading={loginLoading}
      />
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1877F2] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Study Contact Desk</h1>
            <p className="text-white/80 text-sm">Record, organize, and follow up on every website inquiry</p>
          </div>
          <Button 
            variant="outline" 
            className="border-white text-white hover:bg-white/20"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#1877F2]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1877F2]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Requests</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.studyRegistrations}</p>
                <p className="text-xs text-gray-500">Study Registrations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.contactMessages}</p>
                <p className="text-xs text-gray-500">Contact Messages</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.newPending}</p>
                <p className="text-xs text-gray-500">New/Pending</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <Card className="border-0 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search name, email, subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]" data-testid="admin-filter-type">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="join_study">Study Registrations</SelectItem>
                  <SelectItem value="contact">Contact Messages</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]" data-testid="admin-sort">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name-az">Name A-Z</SelectItem>
                  <SelectItem value="name-za">Name Z-A</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={exportToCSV} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="admin-export"
              >
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <SubmissionsTable
          submissions={filteredSubmissions}
          loading={loading}
          onEdit={updateSubmission}
          onDelete={deleteSubmission}
        />
      </main>
    </div>
  );
};

export default AdminPage;
